/**
 * ICS2（ENS）申报凭证
 *
 * 设计来源：设计文档 §7.2.1（ICS2）/ §7.3（区块：申报信息 / 单号 / 运输 / 参与方 / 集装箱 / 货物）
 * - Entry Type = declarationType（F14–F17，D5/A9）
 * - 本产品凭证无 Vessel / Voyage（D6/A2），运输区块只有 Place Of Receipt / Delivery
 * - 港口渲染为「英文名 + 国家 2 位码」（与报文 loadingPort.getNameEn() + code.substring(0,2) 同源）
 * - Carrier EORI ← carrierId；Declarant EORI ← declarantId（联系人，不是 addressList）
 */
import type { CertificateBuilder, CertificateContext, CertificateData, CertificateSection } from '../types'
import { registerCertificateBuilder, CERTIFICATE_TITLES } from './registry'
import {
  DASH,
  FieldCollector,
  dedupeMissing,
  dictCarrierEori,
  dictContactEori,
  dictContainerType,
  dictCountry,
  dictPackingUnit,
  dictPortCountry,
  dictPortName,
  filingStatus,
  findParty,
  formatDateTime,
  gridSection,
  latestReceipt,
  logsSection,
  partyAddress,
  pick,
  receiptValue,
  rowsOf,
  socLabel,
  tableSection,
  text,
  yesNo,
  type CertRow,
} from './shared'

/** 港口英文名 + 国家 2 位码 */
function portLabel(dict: CertificateContext['dict'], id: unknown): string {
  return [dictPortName(dict, id), dictPortCountry(dict, id)].filter(Boolean).join(' ')
}

/** 空箱：优先 emptyContainer 布尔，否则按 containerStatus（EMPTY→Yes） */
function emptyLabel(row: CertRow): string {
  const direct = yesNo(row.emptyContainer)
  if (direct) return direct
  const status = text(row.containerStatus).toUpperCase()
  if (!status) return ''
  return status === 'EMPTY' ? 'Yes' : 'No'
}

export const buildIcs2Certificate: CertificateBuilder = (
  context: CertificateContext
): CertificateData => {
  const detail = context.detail ?? {}
  const dict = context.dict
  const receipt = latestReceipt(context.receipts)
  const missing: string[] = []

  const info = new FieldCollector(missing)
  info
    .add('Entry Type', pick(detail, 'declarationType'))
    .add('Addressed Member State', dictCountry(dict, detail.euEntryCountryId))
    .add('MRN', pick(detail, 'mrnNo'))
    .add(
      'Filing Status',
      filingStatus(
        pick(receipt, 'receiptStatus') || pick(detail, 'customsStatus'),
        context.statusLabel,
        pick(receipt, 'sendType')
      )
    )
    .add('Notification Message', pick(receipt, 'receiptMessage') || pick(detail, 'receiptMessage'))
    .add(
      'Registration Date',
      formatDateTime(pick(receipt, 'receiptTime') || pick(detail, 'receiptTime'))
    )

  const numbers = new FieldCollector(missing)
  numbers
    .add('MBL', pick(detail, 'mblNo'))
    .add('Carrier EORI', dictCarrierEori(dict, detail.carrierId))
    .add('HBL', pick(detail, 'hblNo'))
    .add('Declarant EORI', dictContactEori(dict, detail.declarantId))

  const transport = new FieldCollector(missing)
  transport
    .add('Place Of Receipt', portLabel(dict, detail.loadingPortId))
    .add('Place Of Delivery', portLabel(dict, detail.dischargePortId))

  const parties = new FieldCollector(missing)
  parties
    .add('Shipper', partyAddress(findParty(detail, 'SHIPPER'), { dict }))
    .add('Consignee', partyAddress(findParty(detail, 'CONSIGNEE'), { dict }))
    .add('Seller', partyAddress(findParty(detail, 'SELLER'), { dict }))
    .add('Buyer', partyAddress(findParty(detail, 'BUYER'), { dict }))

  const containerRows = rowsOf(detail, 'containerList').map(row => [
    text(row.containerNo) || DASH,
    dictContainerType(dict, row.containerType) || DASH,
    socLabel(row.eqOwner) || DASH,
    emptyLabel(row) || DASH,
  ])

  const cargoRows = rowsOf(detail, 'cargoList').map(row => [
    text(row.cargoName) || DASH,
    text(row.hsCode) || DASH,
    text(row.quantity) || DASH,
    dictPackingUnit(dict, row.unitTypeId) || DASH,
    text(row.grossWeight) || DASH,
  ])

  const sections: CertificateSection[] = [
    gridSection(2, info.fields, 'DECLARATION'),
    gridSection(4, numbers.fields, 'REFERENCE'),
    gridSection(2, transport.fields, 'TRANSPORT'),
    gridSection(2, parties.fields, 'PARTY INFORMATION'),
    tableSection(
      'CONTAINERS',
      ['Container No.', 'Type', 'SOC', 'Empty'],
      containerRows,
      missing
    ),
    tableSection(
      'CARGOS',
      ['Goods Description', 'HS Code', 'Qty', 'Unit', 'Weight'],
      cargoRows,
      missing
    ),
    logsSection(context.receipts, missing),
  ]

  return {
    product: 'ICS2',
    title: CERTIFICATE_TITLES.ICS2,
    issuedAt: formatDateTime(
      receiptValue(context.receipts, 'receiptTime') ||
        pick(detail, 'receiptTime') ||
        pick(detail, 'dateModified')
    ),
    sections,
    missing: dedupeMissing(missing),
  }
}

registerCertificateBuilder('ICS2', buildIcs2Certificate)
